const SRC = 'use-clips-timestamp.ts';

import { LogLevel } from 'services/logger.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { appLogger } from 'components/app/_mediator/app-logger.slice';
import { selectClipData, isNull, createClipId } from 'components/app/_helpers/_app.helper';
import { handleSbLinks } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { Clip, handleClips, selectStats } from 'components/app/editor/_mediator/editor.slice';
import { useEditorCb } from 'components/app/editor/_mediator/use-editor-cb';
import { clipsApi, handleNewClips } from './editor-clips.helper';
import { isLinksValid } from './editor-clips-linking.helper';
import {
  prepareTimestampReplacementClip,
  resolveTimestampReplacePosition,
  storyboardDeleteDeltaSucceeded,
} from './editor-clips-timestamp.helper';

type Action = 'start' | 'end' | 'split' | 'removeStart' | 'removeEnd';

export const useClipsTimestamp = (editingClip?: ClipId) => {
  const { getPlayerRef } = useEditorCb();
  const { clipsMap, linkedClips, storyBoard, activeClip, selector } = useAppSelector((state) => state.editor);
  const tsLinkedPayload = useAppSelector((state) => state.timestamp.tsLinkedPayload);
  const stats = useAppSelector(selectStats);
  const dispatch = useAppDispatch();

  const createNewClips = (playerTime: EditorTime, editingClips: Clip[], action: Action) => {
    if (!clipsMap) return [];
    const clips: Clip[] = [];
    clips[0] = prepareTimestampReplacementClip(editingClips[0]);
    if (action === 'start') {
      clips[0].startTime = playerTime;
      if (clips[0].endTime === playerTime) clips[0].endTime = null;
      else if (editingClips[1]) {
        clips[1] = prepareTimestampReplacementClip(editingClips[1]);
        clips[1].endTime = playerTime;
      }
    } else if (action === 'end') {
      clips[0].endTime = playerTime;
      if (clips[0].startTime === playerTime) clips[0].startTime = null;
      else if (editingClips[1]) {
        clips[1] = prepareTimestampReplacementClip(editingClips[1]);
        clips[1].startTime = playerTime;
      }
    } else if (action === 'split') {
      clips[1] = { ...clips[0] };
      if (isNull(clips[0].startTime)) {
        clips[0].startTime = playerTime;
        clips[1].endTime = playerTime;
      } else {
        clips[0].endTime = playerTime;
        clips[1].startTime = playerTime;
      }
    } else if (action === 'removeStart') clips[0].startTime = null;
    else if (action === 'removeEnd') clips[0].endTime = null;
    clips.forEach((clip) => (clip.clipId = createClipId(clip)));
    return handleNewClips(clips, playerTime, clipsMap, linkedClips);
  };

  const getPayload = (action: Action) => {
    if (!editingClip || !selector || !clipsMap) return;
    const clip = selectClipData(editingClip, clipsMap);
    const playerTime = getPlayerRef()?.getCurrentTime();
    if (playerTime === undefined) return;
    const editingClips: Clip[] = [clip];
    let isLeftNeighbor = false;
    if (tsLinkedPayload) {
      const { leftNeighbor, rightNeighbor } = tsLinkedPayload;
      if (action === 'start' && clip.startTime && clip.startTime === leftNeighbor?.endTime) {
        editingClips.push(leftNeighbor);
        isLeftNeighbor = true;
      } else if (action === 'end' && clip.endTime && clip.endTime === rightNeighbor?.startTime)
        editingClips.push(rightNeighbor);
    }

    const position = resolveTimestampReplacePosition(editingClips, isLeftNeighbor, storyBoard);
    if (!position) {
      console.error(`[${SRC}] getPayload: clip not on storyboard`);
      dispatch(
        appLogger({
          level: LogLevel.Fatal,
          error: { name: `[${SRC}] getPayload: clip not on storyboard`, message: `editingClip: ${editingClip}` },
        })
      );
      return;
    }

    const delResult = clipsApi(selector, storyBoard, linkedClips).del(editingClips, true);
    if (!storyboardDeleteDeltaSucceeded(delResult.storyBoard)) {
      console.error(`[${SRC}] getPayload: storyboard delete failed`);
      dispatch(
        appLogger({
          level: LogLevel.Fatal,
          error: { name: `[${SRC}] getPayload: storyboard delete failed`, message: `editingClip: ${editingClip}` },
        })
      );
      return;
    }

    const clips = createNewClips(playerTime, editingClips, action);
    if (!clips.length) return;

    return {
      clipsMap: delResult.clipsMap,
      storyBoard: { ...storyBoard, ...delResult.storyBoard },
      linkedClips: { ...linkedClips, ...delResult.linkedClips },
      selector: { ...selector, ...delResult.selector },
      position,
      clips,
    };
  };

  return (action: Action) => {
    const payload = getPayload(action);
    if (!payload) return { reset: true };
    const { linkedClips, storyBoard, selector, clipsMap, position, clips } = payload;
    const res = clipsApi(selector, storyBoard, linkedClips).add(clips, position);
    if (!res) {
      Error.addClip.noStoryboardCell();
      return { reset: true };
    }
    if (!isLinksValid(res.linkedClips)) {
      Error.linkingNotPossible();
      return { reset: true };
    }
    const addingClipsLen = clips.length - Object.values(clipsMap).length;
    if (addingClipsLen > stats!.available) {
      Error.tooManyClips(addingClipsLen, stats!.available);
      return { reset: true };
    }
    const res1 = handleSbLinks(res.linkedClips, res.storyBoard, storyBoard);
    if (res1.status === 'cantBeFixed') {
      Error.handleSbLinks();
      return { reset: true };
    }
    dispatch(
      handleClips({
        data: { ...res, storyBoard: res1.storyBoard, clipsMap: { ...clipsMap, ...res.clipsMap } },
        backupParams: { title: 'Timestamp Clip', activeClip: [activeClip, clips[0].clipId] },
      })
    );
  };
};
